import { z } from "zod";

import { USAGE_MODES } from "@louez/types";

import { businessHoursSchema } from "./business-hours";

/**
 * The contract between the central Reserlo operator panel and one customer
 * environment: exactly which settings the panel may read back and apply.
 *
 * Deliberately a short list. The panel configures a rental company; it does
 * not administer its data. Nothing here touches products, customers,
 * reservations, payments or users, and there is no field that could delete
 * anything. Adding a field is a decision, which is why they are enumerated
 * rather than passed through.
 *
 * Every field is optional: the panel sends what the operator filled in, and
 * an absent field is left exactly as the renter has it. That is what makes
 * applying a half-finished onboarding safe, and what stops an old central
 * copy from silently overwriting a change the renter made themselves.
 */

/** Trimmed, and an empty string means "clear this field". */
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((value) => (value.length === 0 ? null : value))
    .nullable();

const httpUrl = z
  .string()
  .trim()
  .max(2048)
  .refine(
    (value) => {
      if (value.length === 0) return true;
      try {
        const url = new URL(value);
        return url.protocol === "http:" || url.protocol === "https:";
      } catch {
        return false;
      }
    },
    { message: "A website must be an absolute http or https URL" },
  )
  .transform((value) => (value.length === 0 ? null : value))
  .nullable();

/** ISO 3166-1 alpha-2, upper-cased. */
const countryCode = z
  .string()
  .trim()
  .regex(/^[A-Za-z]{2}$/)
  .transform((value) => value.toUpperCase());

export const opsCompanySchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  email: z.email().max(254).nullable().optional(),
  phone: optionalText(40).optional(),
  address: optionalText(400).optional(),
});

export const opsLocaleSchema = z.object({
  country: countryCode.optional(),
  /** IANA zone; validated against the runtime's own tz database on apply. */
  timezone: z.string().trim().min(1).max(64).optional(),
  currency: z
    .string()
    .trim()
    .regex(/^[A-Za-z]{3}$/)
    .transform((value) => value.toUpperCase())
    .optional(),
  /** Storefront fallback language, ISO 639-1. */
  locale: z
    .string()
    .trim()
    .regex(/^[a-z]{2}$/i)
    .transform((value) => value.toLowerCase())
    .nullable()
    .optional(),
});

export const opsUsageSchema = z.object({
  usageMode: z.enum(USAGE_MODES).optional(),
  externalWebsiteUrl: httpUrl.optional(),
});

export const opsRentalRulesSchema = z.object({
  reservationMode: z.enum(["payment", "request"]).optional(),
  /**
   * Whether an unanswered request already blocks stock. The default is true
   * and the consequence is real, so the panel shows it rather than assuming.
   */
  pendingBlocksAvailability: z.boolean().optional(),
  advanceNoticeMinutes: z.number().int().min(0).max(525_600).optional(),
  turnoverBufferMinutes: z.number().int().min(0).max(10_080).optional(),
  minRentalMinutes: z.number().int().min(0).max(525_600).optional(),
  businessHours: businessHoursSchema.optional(),
});

export const opsConfigurationSchema = z.object({
  company: opsCompanySchema.optional(),
  localisation: opsLocaleSchema.optional(),
  usage: opsUsageSchema.optional(),
  rentalRules: opsRentalRulesSchema.optional(),
});

export type OpsConfigurationInput = z.infer<typeof opsConfigurationSchema>;

/**
 * One apply request.
 *
 * `environmentId` and `storeSlug` are the panel saying which environment and
 * which store it believes it is changing. Both are checked against what this
 * environment actually is, so a configuration meant for company A cannot
 * land on company B even if the base URL was wrong.
 */
export const opsApplyRequestSchema = z.object({
  environmentId: z.string().trim().min(1).max(100),
  storeSlug: z.string().trim().min(1).max(100).optional(),
  configuration: opsConfigurationSchema,
});

export type OpsApplyRequest = z.infer<typeof opsApplyRequestSchema>;

// ============================================================================
// Creating the store
// ============================================================================

/**
 * One request to create a rental company's store and its owner.
 *
 * Separate from the configuration contract above on purpose: that one
 * adjusts an existing store and every field is optional, this one brings a
 * store into being and may run exactly once. Mixing them would make "apply
 * a setting" and "create a company" the same call, and a retry of the first
 * would then be able to do the second.
 */
export const opsCreateStoreRequestSchema = z.object({
  environmentId: z.string().trim().min(1).max(100),
  owner: z.object({
    name: z.string().trim().min(1).max(120),
    email: z.email().max(254),
    /** Handed to the renter by the operator; Better Auth stores only a hash. */
    password: z.string().min(12).max(200),
  }),
  store: z.object({
    name: z.string().trim().min(2).max(200),
    slug: z.string().trim().max(100).optional(),
    email: z
      .union([z.email(), z.literal("")])
      .nullable()
      .optional(),
    phone: z.string().trim().max(40).nullable().optional(),
    address: z.string().trim().max(400).nullable().optional(),
    country: countryCode.optional(),
    currency: z
      .string()
      .trim()
      .regex(/^[A-Za-z]{3}$/)
      .transform((value) => value.toUpperCase())
      .optional(),
    timezone: z.string().trim().min(1).max(64).optional(),
    locale: z
      .string()
      .trim()
      .regex(/^[a-z]{2}$/i)
      .transform((value) => value.toLowerCase())
      .nullable()
      .optional(),
  }),
});

export type OpsCreateStoreRequest = z.infer<typeof opsCreateStoreRequestSchema>;
